/**
 * PermissionSettings - Admin page to manage feature permissions
 * Configure which roles can access which features
 */
import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  HiOutlineArrowLeft,
  HiOutlineShieldCheck,
  HiOutlineRefresh,
  HiOutlineSave,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlinePlus,
  HiOutlineTrash,
  HiOutlineExclamation,
} from 'react-icons/hi'
import toast from 'react-hot-toast'
import permissionService from '../../services/permissionService'

// Role config
const ROLES = [
  { key: 'admin', label: 'Admin', color: 'text-red-600', bgColor: 'bg-red-100' },
  { key: 'ceo', label: 'CEO', color: 'text-amber-600', bgColor: 'bg-amber-100' },
  { key: 'leader', label: 'Leader', color: 'text-purple-600', bgColor: 'bg-purple-100' },
  { key: 'sales', label: 'Sales', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  { key: 'guest', label: 'Khách', color: 'text-gray-600', bgColor: 'bg-gray-100' },
]

// Map access level descriptions
const MAP_ACCESS_LEVELS = [
  {
    key: 'public',
    label: 'Không bảo mật',
    color: 'text-green-700',
    bgColor: 'bg-green-100',
    borderColor: 'border-green-200',
    description: 'Ai cũng có thể xem, không cần đăng nhập',
    roles: ['Tất cả mọi người']
  },
  {
    key: 'internal',
    label: 'Nội bộ',
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
    borderColor: 'border-blue-200',
    description: 'Chỉ những người đã đăng nhập mới xem được',
    roles: ['Admin', 'CEO', 'Leader', 'Sales']
  },
  {
    key: 'private',
    label: 'Riêng tư',
    color: 'text-red-700',
    bgColor: 'bg-red-100',
    borderColor: 'border-red-200',
    description: 'Chỉ cấp quản lý mới xem được',
    roles: ['Admin', 'CEO', 'Leader']
  },
]

export default function PermissionSettings() {
  const [permissions, setPermissions] = useState([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)
  const [showAddModal, setShowAddModal] = useState(false)

  // Track original permissions for change detection
  const [originalPermissions, setOriginalPermissions] = useState([])

  useEffect(() => {
    loadPermissions()
  }, [])

  const loadPermissions = async () => {
    setLoading(true)
    const result = await permissionService.getAllPermissions()
    if (result.status === 'success') {
      setPermissions(result.data)
      setOriginalPermissions(JSON.parse(JSON.stringify(result.data)))
      setHasChanges(false)
    }
    setLoading(false)
  }

  // Toggle permission for a role
  const togglePermission = (permissionId, role) => {
    if (role === 'admin' || role === 'ceo') return // Can't change admin/ceo

    setPermissions(prev => prev.map(p => {
      if (p.id === permissionId) {
        const key = `${role}_access`
        return {
          ...p,
          permissions: {
            ...p.permissions,
            [role]: !p.permissions[role]
          }
        }
      }
      return p
    }))
    setHasChanges(true)
  }

  // Save all changes
  const saveChanges = async () => {
    setSaving(true)

    // Build update payload
    const updates = permissions.map(p => ({
      id: p.id,
      leader_access: p.permissions.leader,
      sales_access: p.permissions.sales,
      guest_access: p.permissions.guest,
    }))

    const result = await permissionService.bulkUpdatePermissions(updates)

    if (result.status === 'success') {
      toast.success('Đã lưu thay đổi')
      setOriginalPermissions(JSON.parse(JSON.stringify(permissions)))
      setHasChanges(false)
    } else {
      toast.error(result.message || 'Có lỗi xảy ra')
    }

    setSaving(false)
  }

  // Initialize default permissions
  const initDefaults = async () => {
    const result = await permissionService.initDefaultPermissions()
    if (result.status === 'success') {
      toast.success(result.message)
      loadPermissions()
    } else {
      toast.error(result.message || 'Có lỗi xảy ra')
    }
  }

  // Delete permission
  const deletePermission = async (id) => {
    if (!confirm('Bạn có chắc muốn xóa permission này?')) return

    const result = await permissionService.deletePermission(id)
    if (result.status === 'success') {
      toast.success('Đã xóa')
      loadPermissions()
    } else {
      toast.error(result.message || 'Có lỗi xảy ra')
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3">
          <div className="flex items-center gap-3">
            <Link
              to="/admin/users"
              className="p-2 -ml-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <HiOutlineArrowLeft className="w-5 h-5 text-gray-600" />
            </Link>
            <div className="flex-1">
              <h1 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                <HiOutlineShieldCheck className="w-5 h-5 text-primary-600" />
                Phân quyền tính năng
              </h1>
              <p className="text-sm text-gray-500">
                Cài đặt quyền truy cập cho từng vai trò
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2">
              {permissions.length === 0 && (
                <button
                  onClick={initDefaults}
                  className="flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-amber-700 bg-amber-100 rounded-lg hover:bg-amber-200 transition-colors"
                >
                  <HiOutlinePlus className="w-4 h-4" />
                  Tạo mặc định
                </button>
              )}

              <button
                onClick={loadPermissions}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                title="Tải lại"
              >
                <HiOutlineRefresh className="w-5 h-5" />
              </button>

              {hasChanges && (
                <button
                  onClick={saveChanges}
                  disabled={saving}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50"
                >
                  {saving ? (
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <HiOutlineSave className="w-4 h-4" />
                  )}
                  Lưu thay đổi
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Empty State */}
        {permissions.length === 0 && (
          <div className="bg-white rounded-xl border p-8 text-center">
            <HiOutlineExclamation className="w-12 h-12 text-amber-400 mx-auto mb-3" />
            <h3 className="font-semibold text-gray-900 mb-2">Chưa có cài đặt phân quyền</h3>
            <p className="text-sm text-gray-500 mb-4">
              Nhấn "Tạo mặc định" để khởi tạo các quyền cho các tính năng hiện có
            </p>
            <button
              onClick={initDefaults}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
            >
              <HiOutlinePlus className="w-4 h-4" />
              Tạo permissions mặc định
            </button>
          </div>
        )}

        {/* Permissions Table */}
        {permissions.length > 0 && (
          <div className="bg-white rounded-xl border overflow-hidden">
            {/* Table Header */}
            <div className="grid grid-cols-[1fr_repeat(5,80px)] gap-2 px-4 py-3 bg-gray-50 border-b text-sm font-medium text-gray-600">
              <div>Tính năng</div>
              {ROLES.map(role => (
                <div key={role.key} className="text-center">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${role.bgColor} ${role.color}`}>
                    {role.label}
                  </span>
                </div>
              ))}
            </div>

            {/* Table Body */}
            <div className="divide-y">
              {permissions.map(permission => (
                <div
                  key={permission.id}
                  className="grid grid-cols-[1fr_repeat(5,80px)] gap-2 px-4 py-3 hover:bg-gray-50 transition-colors items-center"
                >
                  {/* Feature Info */}
                  <div>
                    <h4 className="font-medium text-gray-900">{permission.feature_name}</h4>
                    <p className="text-sm text-gray-500">{permission.feature_description}</p>
                    <code className="text-xs text-gray-400 bg-gray-100 px-1 rounded">
                      {permission.feature_key}
                    </code>
                  </div>

                  {/* Role Toggles */}
                  {ROLES.map(role => (
                    <div key={role.key} className="flex justify-center">
                      {role.key === 'admin' || role.key === 'ceo' ? (
                        // Admin and CEO always have access - show locked
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center" title={`${role.label} luôn có quyền`}>
                          <HiOutlineCheck className="w-5 h-5 text-green-600" />
                        </div>
                      ) : (
                        // Toggle button for other roles
                        <button
                          onClick={() => togglePermission(permission.id, role.key)}
                          className={`
                            w-10 h-10 rounded-full flex items-center justify-center transition-all
                            ${permission.permissions[role.key]
                              ? 'bg-green-100 text-green-600 hover:bg-green-200'
                              : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                            }
                          `}
                          title={permission.permissions[role.key] ? 'Có quyền truy cập' : 'Không có quyền'}
                        >
                          {permission.permissions[role.key] ? (
                            <HiOutlineCheck className="w-5 h-5" />
                          ) : (
                            <HiOutlineX className="w-5 h-5" />
                          )}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Legend */}
        <div className="mt-4 flex items-center gap-6 text-sm text-gray-500">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
              <HiOutlineCheck className="w-4 h-4 text-green-600" />
            </div>
            <span>Có quyền truy cập</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
              <HiOutlineX className="w-4 h-4 text-gray-400" />
            </div>
            <span>Không có quyền</span>
          </div>
        </div>

        {/* Map Access Levels Section */}
        <div className="mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <HiOutlineShieldCheck className="w-5 h-5 text-primary-600" />
            Mức độ bảo mật Bản đồ giá
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Mỗi bản đồ giá có thể được cài đặt mức độ bảo mật riêng tại trang chỉnh sửa bản đồ.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {MAP_ACCESS_LEVELS.map(level => (
              <div
                key={level.key}
                className={`p-4 rounded-xl border ${level.borderColor} ${level.bgColor}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <span className={`font-semibold ${level.color}`}>{level.label}</span>
                  <code className="text-xs text-gray-500 bg-white/50 px-1.5 py-0.5 rounded">
                    {level.key}
                  </code>
                </div>
                <p className={`text-sm ${level.color} mb-3`}>
                  {level.description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {level.roles.map(role => (
                    <span
                      key={role}
                      className="text-xs px-2 py-0.5 bg-white/70 rounded-full text-gray-600"
                    >
                      {role}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <p className="text-sm text-amber-700">
              <strong>Lưu ý:</strong> Để thay đổi mức độ bảo mật của một bản đồ, vào{' '}
              <Link to="/admin/market-maps" className="underline hover:text-amber-900">
                Quản lý bản đồ giá
              </Link>
              {' '}→ Chỉnh sửa bản đồ → Mức độ bảo mật.
            </p>
          </div>
        </div>

        {/* Help text */}
        <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
          <h4 className="font-medium text-blue-900 mb-2">Hướng dẫn vai trò</h4>
          <ul className="text-sm text-blue-700 space-y-1">
            <li>• <strong>Admin</strong>: Quản trị viên, có quyền truy cập tất cả tính năng</li>
            <li>• <strong>CEO</strong>: Giám đốc điều hành, quyền cao cấp</li>
            <li>• <strong>Leader</strong>: Trưởng nhóm, có thể quản lý bản đồ giá</li>
            <li>• <strong>Sales</strong>: Nhân viên kinh doanh</li>
            <li>• <strong>Khách</strong>: Người dùng chưa đăng nhập (guest)</li>
          </ul>
        </div>
      </div>

      {/* Unsaved Changes Warning */}
      {hasChanges && (
        <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 shadow-lg flex items-center gap-3">
          <HiOutlineExclamation className="w-5 h-5 text-amber-500" />
          <span className="text-sm text-amber-700">Bạn có thay đổi chưa lưu</span>
          <button
            onClick={saveChanges}
            disabled={saving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-primary-600 rounded-lg hover:bg-primary-700 transition-colors"
          >
            Lưu ngay
          </button>
        </div>
      )}
    </div>
  )
}
